import React, { useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
// Hooks
import useCopyToClipboard from 'app/hooks/useCopyToClipboard';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Models
import { IDocument } from 'app/store/DMSDocuments/DMSDocuments.models';
import { IPageDuplicate } from '@root/models/Page';
import { RootState } from 'app/store';
// Async
import { updateDuplicates } from 'app/store/Cases/Cases.async';
// Selectors
import { selectDocumentEntity } from 'app/store/DMSDocuments/DMSDocuments.selectors';
import { selectDuplicatesList, selectLoading } from 'app/store/Cases/Cases.selectors';
// MaterialUI
import { Box, Theme, Typography, Tooltip } from '@mui/material';
// Icons
import {
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
// Dialogs
import ConfirmationDialog from 'dialogs/Confirmation.dialog';
// Components
import PageImage from '@root/components/PageImage';
import { Bull } from 'app/components/Utilities';
import { Button, IconButton } from 'app/components/Mui';
// Mixpanel
import MixpanelTracks from 'app/types/MixpanelTracks';
import Mixpanel from 'app/services/Mixpanel.service';
// i18next
import { useTranslation } from 'react-i18next';

type Props = {
  documentId: number;
  pageNum: number;
  isOriginal?: boolean;
};

const PageCompareDuplicatesItem:React.FC<Props> = ({
  // Props
  documentId, pageNum, isOriginal
}) => {
  const { t } = useTranslation();

  const { caseId } = useParams<{ caseId:string }>();
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const documentEntity = useAppSelector((state:RootState) => selectDocumentEntity(state, { documentId })) as IDocument;
  const duplicates = useAppSelector(selectDuplicatesList);
  const isLoading:boolean = useAppSelector(selectLoading);

  const [ asyncCopyTextToClipboardFn, copied ] = useCopyToClipboard();

  const handleClick = useCallback(async () => {
    try {
      await asyncCopyTextToClipboardFn(text);
    } catch(error){
      console.log(error);
    }
    // eslint-disable-next-line
  }, []);

  const toDocument = `/admin/cases/${caseId}/documents/${documentId}`;
  const toPage = `${toDocument}/${pageNum}`;
  const text = `${window.location.origin}${toPage}`;

  const [ dialogOpen, setDialogOpen ] = React.useState<boolean>(false);

  const toggleDialog = () => setDialogOpen((prevState:boolean) => !prevState);

  const handleConfirm = async () => {
    if ( !duplicates ) return;

    // const duplicatesToUpdate = duplicates.map((cluster:Array<IPageDuplicate>) =>
    //   cluster.filter((page:IPageDuplicate) => {
    //     if ( page.documentId === documentId && page.pageNum === pageNum ) return false;
    //     return true;
    //   })
    // ).filter((cluster:Array<IPageDuplicate>) => cluster.length > 1);

    const duplicatesToUpdate:IPageDuplicate[][] = [];

    const notDuplicatePageIds:string[] = [];
    const duplicatedPageIds:string[] = [];

    for ( let cluster of Object.values(duplicates) ){
      const nextCluster = [];
      for ( let page of cluster ){
        if (
          page.documentId === documentId &&
          page.pageNum === pageNum
        ){
          notDuplicatePageIds.push(`${documentId}:${pageNum}`);
          continue;
        } else {
          duplicatedPageIds.push(`${page.documentId}:${page.pageNum}`);
          nextCluster.push(page);
        }
      }
      if ( nextCluster.length > 1 ){
        duplicatesToUpdate.push(nextCluster);
      }
    }

    await dispatch(updateDuplicates({
      insuranceCaseId: Number(caseId),
      data: duplicatesToUpdate,
      message: t('notifications.cardPage.pageDeleteFromDuplicates')
    })).unwrap();

    Mixpanel.track(MixpanelTracks.PageMarkedNotADUplicate, {
      caseId: Number(caseId),
      // Deleted
      notDuplicatePageIds,
      // Remaining
      duplicatedPageIds
    });
  }

  return (
    <React.Fragment>
      <Box sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        padding: '8px 0'
      }}>
        <Box>
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            <Box
              sx={{
                textDecoration: 'none',
                color: 'rgba(0,0,0,0.6)',
                overflow: 'hidden',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
              component={Link}
              to={toDocument} target="_blank"
            >
              <Typography>{documentEntity.name}</Typography>
            </Box>
            <Bull />
            <Box
              sx={{
                whiteSpace: 'nowrap',
                textDecoration: 'none',
                color: 'rgba(0,0,0,0.6)',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
              component={Link}
              to={toPage} target="_blank"
            ><Typography>{t('labels.page')} {pageNum}</Typography></Box>
            <Tooltip
              sx={{
                ml: 2,
                '&:hover': {
                  cursor: 'pointer',
                  color: (theme:Theme) => theme.palette.primary.main
                }
              }}
              title={copied ? t('labels.copied') : t('buttons.copyLink')}
              aria-label={undefined}
            >
              <IconButton
                name="Copy page link"
                aria-label="Copy page link button"
                onClick={handleClick}
                size="small"
              >
                {copied ? (
                  <CheckIcon color="success" fontSize="small" />
                ) : (
                  <ContentCopyIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Box>
          {isOriginal ? (
            <span style={{
              display: 'flex',
              alignItems: 'center'
            }}>{t('labels.original')}</span>
          ) : (
            <span style={{
              display: 'flex',
              alignItems: 'center'
            }}>&nbsp;</span>
          )}
        </Box>
        {isOriginal ? (
          <Button
            name="Not a duplicate"
            onClick={toggleDialog}
            color="error"
            size="small"
            sx={{ mr: 4, py: 1.25 }}
          >{t('dialogs.pageCompare.notDuplicateButton')}</Button>
        ) : null}
      </Box>
      <div style={{
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: '4px',
        marginBottom: '8px',
        overflow: 'hidden'
      }}>
        <PageImage
          documentId={documentId}
          pageNum={pageNum}
        />
      </div>
      {dialogOpen ? (
        <ConfirmationDialog
          open={dialogOpen}
          onClose={toggleDialog}
          status={'initial'}
          loading={isLoading}
          title={t('dialogs.pageCompare.notDuplicateTitle')}
          content={t('dialogs.pageCompare.notDuplicateConfirmation')}
          onConfirm={handleConfirm}
        />
      ) : null}
    </React.Fragment>
  )
}

export default PageCompareDuplicatesItem;
